/**
 * <bundle-components> — fetches a fixed bundle's components from the Storefront
 * API and renders them into the list, replacing the server-rendered fallback.
 * Quantity is intentionally never displayed. On any failure the fallback stays.
 */
if (!customElements.get('bundle-components')) {
  const COMPONENTS_QUERY = `
    query BundleComponents($gid: ID!) {
      node(id: $gid) {
        ... on ProductVariant {
          components(first: 30) {
            nodes {
              quantity
              productVariant {
                product {
                  handle
                  title
                  onlineStoreUrl
                  featuredImage { url altText }
                  ingredients: metafield(namespace: "custom", key: "ingredients") { value }
                  allergens: metafield(namespace: "custom", key: "allergens") { value }
                }
              }
            }
          }
        }
      }
    }`;

  class BundleComponents extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-bundle-list]');
      this.render(this.dataset.variantGid);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event && event.data && event.data.variant;
          if (variant && variant.id) {
            this.render('gid://shopify/ProductVariant/' + variant.id);
          }
        });
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    async render(gid) {
      const token = this.dataset.storefrontToken;
      if (!token || !gid || !window.StockedBundleComponents) return; // keep fallback

      this.classList.add('is-loading');
      try {
        const components = await this.fetchComponents(gid, token);
        if (!components.length) return; // keep fallback
        // The Storefront API gives no control over component order, so impose the
        // merchant-defined order passed down from the bundle_component_order metafield.
        const order = (this.dataset.componentOrder || '')
          .split(',')
          .map((handle) => handle.trim())
          .filter(Boolean);
        const ordered = window.StockedBundleComponents.sortComponents(components, order);
        this.body.innerHTML = window.StockedBundleComponents.componentList(ordered, {
          showImages: this.dataset.showImages !== 'false',
        });
        this.classList.add('is-loaded');
      } catch (error) {
        console.warn('[bundle-components] failed to load components; keeping fallback', error);
      } finally {
        this.classList.remove('is-loading');
      }
    }

    async fetchComponents(gid, token) {
      const endpoint =
        'https://' + this.dataset.shopDomain + '/api/' + (this.dataset.apiVersion || '2025-01') + '/graphql.json';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: COMPONENTS_QUERY, variables: { gid: gid } }),
      });
      if (!response.ok) throw new Error('Storefront API HTTP ' + response.status);

      const json = await response.json();
      if (json.errors) throw new Error('Storefront API errors: ' + JSON.stringify(json.errors));

      const nodes =
        (json.data && json.data.node && json.data.node.components && json.data.node.components.nodes) || [];
      return nodes.map((node) => {
        const product = node.productVariant && node.productVariant.product;
        if (!product) return null;
        return {
          handle: product.handle,
          title: product.title,
          url: product.onlineStoreUrl,
          imageUrl: product.featuredImage ? product.featuredImage.url : '',
          ingredients: product.ingredients ? product.ingredients.value : '',
          allergens: product.allergens ? product.allergens.value : '',
        };
      }).filter(Boolean);
    }
  }

  customElements.define('bundle-components', BundleComponents);
}
